import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Footer from "../components/footer";
import TrainerCarousel from "../components/trainerCarousel";
import LocationMap from "../components/locationMap";
import ContactForm from "../components/contactForm";
import RoomSwitcher from "../components/roomSwitcher";
import ScheduleSoon, { ScheduleSkeleton } from "../components/scheduleSoon";
import NotFound from "./notFound";
import Event from "./event";
import { SeoLocation } from "../components/Seo";

import {
  usePage,
  usePages,
  useSchedule,
  sessionsForRoom,
  groupsForPage,
  instructorsForPage,
} from "../data/useLocations";

import "../styles/location.modules.css";
import "../styles/schedule.modules.css";

// ── STAŁE POZA KOMPONENTEM ───────────────────────────────────────────────────

const WEEK_DAYS = [
  { day: "Poniedziałek", id: 1 },
  { day: "Wtorek", id: 2 },
  { day: "Środa", id: 3 },
  { day: "Czwartek", id: 4 },
  { day: "Piątek", id: 5 },
  { day: "Sobota", id: 6 },
  { day: "Niedziela", id: 7 },
];

const EXP_SHORT = {
  "początkujący/średniozaawansowany": "początk./średniozaaw.",
  "poczatkujący/średniozaawansowany": "początk./średniozaaw.",
  "poczatkujący/sredniozaawansowany": "początk./średniozaaw.",
  "początkujący/sredniozaawansowany": "początk./średniozaaw.",
  "sredniozaawansowany/zaawansowany": "średniozaaw./zaawans.",
  "średniozaawansowany/zaawansowany": "średniozaaw./zaawans.",
};

const NAME_MAP = {
  "aerial hammock": "Akrobatyka na szarfach",
  "aerial hoop": "Akrobatyka na kołach",
  "aerial hoop/hammock": "Akrobatyka na szarfach/kołach",
};

const expShort = (exp) => EXP_SHORT[(exp || "").toLowerCase().trim()] || exp;
const nameChange = (name) => NAME_MAP[(name || "").toLowerCase().trim()] || name;

function ageLabel(minAge, maxAge, adults) {
  if (minAge == null && maxAge == null) return adults ? "Dorośli" : "";
  if (maxAge == null || maxAge >= 99) {
    return adults ? `Dorośli ${minAge ?? 16}+` : `${minAge ?? 0}+ lat`;
  }
  return `${minAge ?? 0}-${maxAge} lat`;
}

function priceLabel(value, suffix) {
  if (value == null) return null;
  return `${value} zł${suffix}`;
}

/** Ile razy w tygodniu grupa ma zajęcia w salach tej podstrony. */
function weeklyCount(group, roomIds) {
  const ids = new Set(roomIds);
  return (group.sessions || []).filter((s) => ids.has(s?.locationId)).length;
}

/**
 * Terminy grupy poza salami tej podstrony, zliczone per lokalizacja.
 * Cena jest za całą grupę, więc rodzic musi wiedzieć, że drugi trening
 * odbywa się gdzie indziej.
 */
function elsewhereSessions(group, roomIds, pagesBySlug, locationPage) {
  const ids = new Set(roomIds);
  const counts = new Map();

  for (const s of group.sessions || []) {
    if (!s?.locationId || ids.has(s.locationId)) continue;

    const page = locationPage.get(s.locationId);
    const label = page ? page.name : "innej lokalizacji";
    counts.set(label, (counts.get(label) || 0) + 1);
  }

  return Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
}

/** "ul. Dąbrowskiego 34/1u, Wrocław" → "Dąbrowskiego 34/1u" */
function streetFromAddress(address) {
  const first = String(address || "").split(",")[0] || "";
  return first.replace(/^\s*(ul\.|al\.|os\.|pl\.)\s*/i, "").trim();
}

/** Odmiana nazwy lokalizacji po "na": "Gajowicka 177" → "na Gajowickiej 177". */
function inLocationLabel(name) {
  const n = String(name || "").trim();
  if (!n) return "w innej lokalizacji";

  const first = n.split(" ")[0];
  const rest = n.slice(first.length).trim();

  // Nazwy ulic zakończone na -a odmieniają się na -ej (Gajowicka → Gajowickiej)
  if (/a$/i.test(first)) {
    return `na ${first.slice(0, -1)}ej${rest ? ` ${rest}` : ""}`;
  }

  return `w ${n}`;
}

/**
 * Pozycje cennika: grupy scalone po nazwie, cenie miesięcznej i częstotliwości.
 * Akrobatyka w trzech grupach wiekowych po 170 zł 1x w tygodniu = jedna pozycja.
 * Ta sama akrobatyka 2x w tygodniu z inną ceną = pozycja osobna.
 */
function buildCourseItems(groups, roomIds, locationPage) {
  const map = new Map();

  for (const g of groups) {
    if (weeklyCount(g, roomIds) === 0) continue;

    // Cena jest za całą grupę, więc liczymy wszystkie jej terminy.
    const total = (g.sessions || []).length;
    const elsewhere = elsewhereSessions(g, roomIds, null, locationPage);

    const name = nameChange(g.name);
    const elsewhereKey = elsewhere
      .map((e) => `${e.label}:${e.count}`)
      .sort()
      .join(",");

    const expKey = g.category === "tournament" ? g.exp ?? "" : "";
    const key = `${name}|${g.priceMonthly ?? "null"}|${total}|${elsewhereKey}|${g.category ?? ""}|${expKey}`;

    if (!map.has(key)) {
      map.set(key, {
        id: key,
        name,
        count: total,
        elsewhere,
        category: g.category || null,
        exp: g.exp || null,
        price: priceLabel(g.priceMonthly, " / mies."),
        tags: new Set((g.tags || []).filter((t) => t !== "Wejście próbne")),
      });
    } else {
      (g.tags || [])
        .filter((t) => t !== "Wejście próbne")
        .forEach((t) => map.get(key).tags.add(t));
    }
  }

  return Array.from(map.values())
    .map((x) => {
      const extra = x.elsewhere
        .map((e) => `${e.count}x ${inLocationLabel(e.label)}`)
        .join(", ");

      const frequency = extra
        ? `${x.count}x w tyg (w tym ${extra})`
        : `${x.count}x w tyg`;

      const level =
        x.category === "tournament" && x.exp ? `${x.exp.trim()} · ` : "";

      return {
        ...x,
        tags: Array.from(x.tags),
        note: `${level}${frequency}`,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "pl") || a.count - b.count);
}

/**
 * Wejścia próbne jako osobne pozycje, scalone po cenie.
 * Jedna cena dla wszystkich → jedna pozycja bez opisu.
 * Różne ceny → osobne pozycje z nazwami zajęć w opisie.
 */
function buildTrialItems(groups, roomIds) {
  const byPrice = new Map();

  for (const g of groups) {
    if (g.priceTrial == null) continue;
    if (weeklyCount(g, roomIds) === 0) continue;

    const key = String(g.priceTrial);
    if (!byPrice.has(key)) {
      byPrice.set(key, { price: g.priceTrial, names: new Set(), tags: new Set() });
    }

    byPrice.get(key).names.add(nameChange(g.name));
    (g.tags || [])
      .filter((t) => t !== "Wejście próbne")
      .forEach((t) => byPrice.get(key).tags.add(t));
  }

  const entries = Array.from(byPrice.values());
  const single = entries.length === 1;

  return entries
    .sort((a, b) => a.price - b.price)
    .map((x) => {
      const names = Array.from(x.names).sort((a, b) => a.localeCompare(b, "pl"));
      const label =
        names.length > 3 ? `${names.slice(0, 3).join(", ")} i inne` : names.join(", ");

      return {
        id: `trial-${x.price}`,
        name: "Wejście próbne",
        tags: Array.from(x.tags),
        note: single ? "1x" : label,
        price: priceLabel(x.price, ""),
      };
    });
}

function sortByStartTime(a, b) {
  const ta = (a?.startTime || "0:0").split(":").map(Number);
  const tb = (b?.startTime || "0:0").split(":").map(Number);
  return ta[0] - tb[0] || ta[1] - tb[1];
}

// ── KOMPONENT ────────────────────────────────────────────────────────────────

export default function Location() {
  const { slug } = useParams();

  const { page, loading, notFound } = usePage(slug);
  const { pages } = usePages();
  const { data } = useSchedule();

  const groups = data?.groups || [];
  const allInstructors = data?.instructors || [];
  const allTags = data?.tags || [];

  const [activeRoomId, setActiveRoomId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("Wszystkie");

  const color = page?.color || "#C2001C";

  // ── SALE ───────────────────────────────────────────────────────────────────
  // Każda sala z informacją, czy ma jakiekolwiek zajęcia.

  const rooms = useMemo(() => {
    if (!page) return [];

    return page.rooms.map((r) => {
      const count = sessionsForRoom(groups, r.id).length;
      return {
        id: r.id,
        label: r.label,
        count,
        status: count > 0 ? "published" : "soon",
      };
    });
  }, [page, groups]);

  /** Domyślnie pierwsza sala z grafikiem, żeby nie otwierać pustej zakładki. */
  useEffect(() => {
    if (loading || rooms.length === 0) return;

    const stillValid = rooms.some((r) => r.id === activeRoomId);
    if (stillValid) return;

    const preferred = rooms.find((r) => r.status === "published") || rooms[0];
    setActiveRoomId(preferred.id);
  }, [loading, rooms, activeRoomId]);

  const activeRoom = useMemo(
    () => rooms.find((r) => r.id === activeRoomId) || rooms[0] || null,
    [rooms, activeRoomId]
  );

  const hasMultipleRooms = rooms.length > 1;

  // ── GRAFIK ─────────────────────────────────────────────────────────────────

  const scheduleByDay = useMemo(() => {
    if (!activeRoom) return [];

    const sessions = sessionsForRoom(groups, activeRoom.id);

    return WEEK_DAYS.map((d) => ({
      ...d,
      items: sessions.filter((s) => s.dayOfWeek === d.id).sort(sortByStartTime),
    })).filter((d) => d.items.length > 0);
  }, [groups, activeRoom]);

  const scheduleStatus = loading
    ? "loading"
    : activeRoom?.status === "published"
    ? "published"
    : "soon";

  // ── CENNIK ─────────────────────────────────────────────────────────────────
  // Budowany z grup mających terminy w którejkolwiek sali tej podstrony.

  const pageGroups = useMemo(
    () => (page ? groupsForPage(groups, page.roomIds) : []),
    [groups, page]
  );

  /**
   * ID sali → etykieta do opisu cennika.
   * Bierzemy ulicę z adresu, bo nazwa podstrony przy lokalizacji
   * wielosalowej to miasto ("Wrocław"), a nazwa sali ("Sala STARS")
   * nic rodzicowi nie mówi.
   */
  const locationPage = useMemo(() => {
    const map = new Map();
    pages.forEach((p) => {
      p.rooms.forEach((r) => {
        map.set(r.id, { name: streetFromAddress(p.address) || p.name });
      });
    });
    return map;
  }, [pages]);

  const filters = useMemo(() => {
    const present = new Set(pageGroups.flatMap((g) => g.tags || []));
    return ["Wszystkie", ...allTags.filter((t) => present.has(t))];
  }, [pageGroups, allTags]);

  const pricing = useMemo(() => {
    if (!page) return [];

    const courses = buildCourseItems(pageGroups, page.roomIds, locationPage);
    const trials = buildTrialItems(pageGroups, page.roomIds);

    const all = [...courses, ...trials];

    if (activeFilter === "Wszystkie") return all;

    if (activeFilter === "Wejście próbne") return trials;

    return all.filter((x) => (x.tags || []).includes(activeFilter));
  }, [pageGroups, page, activeFilter, locationPage]);

  // ── KADRA ──────────────────────────────────────────────────────────────────

  const pageInstructors = useMemo(
    () => (page ? instructorsForPage(groups, allInstructors, page.roomIds) : []),
    [groups, allInstructors, page]
  );

  // ── DANE DLA FORMULARZA ────────────────────────────────────────────────────

  const leadLocation = useMemo(() => {
    if (!page) return null;

    const interests = Array.from(
      new Set(pageGroups.map((g) => nameChange(g.name)).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "pl"));

    return {
      id: activeRoom?.id ?? null,
      name:
        hasMultipleRooms && activeRoom
          ? `${page.name} - ${activeRoom.label}`
          : page.name,
      slug: page.slug,
      color: page.color,
      area: page.area,
      interests,
    };
  }, [page, activeRoom, hasMultipleRooms, pageGroups]);

  // ── DANE DLA SEO ───────────────────────────────────────────────────────────

  const seoLocation = useMemo(() => {
    if (!page) return null;

    return {
      slug: page.slug,
      name: page.name,
      city: page.city,
      address: page.address,
      photo: page.photoUrl,
      classes: pageGroups.map((g) => ({ name: nameChange(g.name) })),
    };
  }, [page, pageGroups]);

  // ── POZOSTAŁE PLACÓWKI ─────────────────────────────────────────────────────

  const otherColumns = useMemo(() => {
    const others = pages.filter((p) => p.slug !== slug);
    const cols = [[], [], []];
    others.forEach((p, i) => cols[i % 3].push(p));
    return cols;
  }, [pages, slug]);

  // ── STANY BRZEGOWE ─────────────────────────────────────────────────────────

  if (loading) return <LocationSkeleton />;

  // Wydarzenia dzielą przestrzeń adresów z lokalizacjami.
  if (!page && (data?.events || []).some((e) => e.slug === slug)) {
    return <Event />;
  }

  if (notFound) return <NotFound />;

  // ── WIDOK ──────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        gap: "50px",
      }}
    >
      <SeoLocation location={seoLocation} />

      <div className="carousel-wrapper" style={{ height: "380px" }}>
        <div
          className="carouselImage-wrapper"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.4)), url(${page?.photoUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            height: "380px",
            paddingTop: "0px",
          }}
        >
          <h1
            className="item-title"
            style={{
              fontWeight: "bold",
              textShadow:
                "0 2px 6px rgba(0,0,0,0.55), 0 8px 18px rgba(0,0,0,0.55), 0 16px 36px rgba(0,0,0,0.45), 0 28px 60px rgba(0,0,0,0.35)",
            }}
          >
            {page?.name}
          </h1>
          <h2
            className="item-desc"
            style={{
              fontWeight: "BOLD",
              textShadow:
                "0 2px 6px rgba(0,0,0,0.55), 0 8px 18px rgba(0,0,0,0.55), 0 16px 36px rgba(0,0,0,0.45), 0 28px 60px rgba(0,0,0,0.35)",
              marginTop: "-5px",
            }}
          >
            {page?.address}
          </h2>
        </div>
      </div>

      {/* O PLACÓWCE */}
      {page?.description ? (
        <>
          <h3
            style={{
              color: "rgb(30,30,30)",
              fontSize: "25px",
              fontWeight: "bold",
              marginBottom: "-25px",
              marginTop: "-10px",
            }}
          >
            O placówce
          </h3>
          <section
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "80%",
              maxWidth: "1200px",
            }}
          >
            <p
              style={{
                color: "rgb(30,30,30)",
                fontSize: "15px",
                textAlign: "center",
                whiteSpace: "pre-line",
              }}
            >
              {page.description}
            </p>
          </section>
        </>
      ) : null}

      <hr
        style={{
          border: `2px solid ${color}`,
          width: "80%",
          borderRadius: "10px",
          marginTop: "-15px",
          marginBottom: "-15px",
        }}
      />

      {/* GRAFIK */}
      <section
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          gap: "20px",
          paddingBottom: "30px",
        }}
      >
        <h3
          style={{
            color: "rgb(30,30,30)",
            fontSize: "25px",
            fontWeight: "bold",
            marginBottom: "10px",
          }}
        >
          Grafik zajęć
        </h3>

        {hasMultipleRooms ? (
          <RoomSwitcher
            rooms={rooms}
            activeId={activeRoom?.id ?? null}
            onChange={setActiveRoomId}
            color={color}
          />
        ) : null}

        {scheduleStatus === "loading" ? <ScheduleSkeleton color={color} /> : null}

        {scheduleStatus === "soon" ? (
          <ScheduleSoon
            color={color}
            note="Grafik dla tej sali pojawi się wkrótce."
            seasonLabel=""
            locationName={
              hasMultipleRooms && activeRoom
                ? `${page?.name} - ${activeRoom.label}`
                : page?.name
            }
          />
        ) : null}

        {scheduleStatus === "published" ? (
          <div
            className="schedule_wrap"
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              alignItems: "center",
              overflowX: "auto",
            }}
          >
            {scheduleByDay.map((d) => (
              <div
                key={d.id}
                className="schedule_day"
                style={{
                  width: "92%",
                  maxWidth: "520px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  paddingBottom: "30px",
                }}
              >
                <div
                  style={{
                    background: color,
                    color: "rgb(250,250,250)",
                    fontWeight: "900",
                    fontSize: "15px",
                    padding: "10px 12px",
                    borderRadius: "14px",
                    boxShadow: "0 8px 22px rgba(0,0,0,0.10)",
                  }}
                >
                  {d.day}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {d.items.map((s) => (
                    <div
                      key={s.key}
                      className="schedule_card"
                      style={{
                        width: "100%",
                        position: "relative",
                        background: "rgba(255,255,255,0.92)",
                        borderTop: "1px solid rgba(0,0,0,0.08)",
                        borderRight: "1px solid rgba(0,0,0,0.08)",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                        borderLeft: `10px solid ${color}`,
                        borderRadius: "16px",
                        padding: "12px 14px",
                        boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
                        textAlign: "left",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        className="schedule_meta_div"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                        }}
                      >
                        <div
                          className="schedule_time"
                          style={{
                            fontSize: "12px",
                            fontWeight: "900",
                            color: "rgba(0,0,0,0.88)",
                          }}
                        >
                          {s.startTime}-{s.endTime}
                        </div>
                        <div
                          className="schedule_name"
                          style={{
                            marginTop: "4px",
                            fontSize: "12px",
                            fontWeight: "900",
                            color: "rgba(0,0,0,0.92)",
                          }}
                        >
                          {nameChange(s.groupName)}
                        </div>
                      </div>

                      <div
                        className="schedule_meta_div"
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <div
                          className="schedule_meta"
                          style={{
                            marginTop: "4px",
                            fontSize: "10px",
                            fontWeight: "700",
                            color: "rgba(0,0,0,0.60)",
                            lineHeight: "1.25",
                          }}
                        >
                          {ageLabel(s.minAge, s.maxAge, s.adults)}
                        </div>
                        <div
                          className="schedule_meta"
                          style={{
                            marginTop: "4px",
                            fontSize: "10px",
                            fontWeight: "700",
                            color: "rgba(0,0,0,0.60)",
                            lineHeight: "1.25",
                          }}
                        >
                          {expShort(s.exp)}
                        </div>
                        <div
                          className="schedule_meta"
                          style={{
                            marginTop: "4px",
                            fontSize: "10px",
                            fontWeight: "700",
                            color: color,
                            lineHeight: "1.25",
                          }}
                        >
                          {s.instructor ? ` ${s.instructor}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {/* KADRA */}
      <h3
        style={{
          color: "rgb(30,30,30)",
          fontSize: "25px",
          fontWeight: "bold",
          marginBottom: "-5px",
          marginTop: "-20px",
        }}
      >
        Kto prowadzi tu zajęcia?
      </h3>
      <section
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          gap: "20px",
          paddingBottom: "50px",
        }}
      >
        <TrainerCarousel color={color} instructors={pageInstructors} />
      </section>

      {/* CENNIK */}
      {pricing.length > 0 ? (
        <>
          <h3
            style={{
              color: "rgb(30,30,30)",
              fontSize: "25px",
              fontWeight: "bold",
              marginBottom: "-25px",
              marginTop: "-25px",
            }}
          >
            Cennik
          </h3>
          <section
            className="pricing_section"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              overflowX: "auto",
              paddingBottom: "40px",
            }}
          >
            <div
              className="pricing_filters"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                gap: "5px",
                paddingBottom: "6px",
                flexWrap: "wrap",
              }}
            >
              {filters.map((f) => (
                <button
                  className="pricing_filter_btn"
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  style={{
                    flex: "0 0 auto",
                    border:
                      activeFilter === f
                        ? `1px solid ${color}`
                        : "1px solid rgba(0,0,0,0.12)",
                    backgroundColor:
                      activeFilter === f ? color : "rgba(255,255,255,0.95)",
                    color: activeFilter === f ? "rgb(250,250,250)" : "rgba(0,0,0,0.85)",
                    borderRadius: "999px",
                    cursor: "pointer",
                    fontWeight: "700",
                  }}
                >
                  {f}
                </button>
              ))}
            </div>

            <div
              className="pricing_list"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                width: "100%",
              }}
            >
              {pricing.map((x) => (
                <div
                  key={x.id}
                  className="pricing_item"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    backgroundColor: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: "18px",
                    padding: "14px 16px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                  }}
                >
                  <div
                    className="pricing_left"
                    style={{ display: "flex", flexDirection: "column", gap: "6px" }}
                  >
                    <div className="pricing_head">
                      <span
                        className="pricing_name"
                        style={{ fontWeight: "800", color: "#111" }}
                      >
                        {x.name}
                      </span>
                      <div className="pricing_badges">
                        {x.tags.map((b, idx) => (
                          <span
                            key={idx}
                            className="pricing_badge"
                            style={{
                              fontWeight: "800",
                              color: color,
                              backgroundColor: "rgba(30,30,30,0.1)",
                              borderRadius: "999px",
                            }}
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div
                      className="pricing_note"
                      style={{ color: "rgba(0,0,0,0.6)", fontWeight: "600" }}
                    >
                      {x.note}
                    </div>
                  </div>

                  <div
                    className="pricing_right"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "8px",
                    }}
                  >
                    <div
                      className="pricing_price"
                      style={{ fontWeight: "900", color: "#111" }}
                    >
                      {x.price || ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}

      <hr
        style={{
          border: `2px solid ${color}`,
          width: "80%",
          borderRadius: "10px",
          marginTop: "-15px",
        }}
      />

      {/* MAPA */}
      <h3
        style={{
          color: "rgb(30,30,30)",
          fontSize: "25px",
          fontWeight: "bold",
          marginBottom: "-25px",
        }}
      >
        Mapa lokalizacji
      </h3>
      <section
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "90%",
          gap: "30px",
          overflowX: "auto",
          flexWrap: "no-wrap",
          maxWidth: "1200px",
        }}
      >
        <LocationMap location={page} />
      </section>

      <hr
        style={{
          border: `2px solid ${color}`,
          width: "80%",
          borderRadius: "10px",
          marginTop: "-25px",
        }}
      />

      {/* FORMULARZ */}
      <h3
        id="kontakt"
        style={{
          color: "rgb(30,30,30)",
          fontSize: "25px",
          fontWeight: "bold",
          marginBottom: "-45px",
          scrollMarginTop: "100px",
        }}
      >
        Zgłoś się na zajęcia!
      </h3>
      <section
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "90%",
          gap: "30px",
          overflowX: "auto",
          flexWrap: "no-wrap",
          maxWidth: "1200px",
        }}
      >
        <ContactForm locationObj={leadLocation} />
      </section>

      <hr
        style={{
          border: `2px solid ${color}`,
          width: "80%",
          borderRadius: "10px",
          marginTop: "-45px",
        }}
      />

      {/* POZOSTAŁE PLACÓWKI */}
      <h3
        style={{
          color: "rgb(30,30,30)",
          fontSize: "25px",
          fontWeight: "bold",
          marginBottom: "-15px",
          marginTop: "-25px",
        }}
      >
        Pozostałe placówki
      </h3>
      <section
        className="locationsSections"
        style={{ display: "flex", alignItems: "flex-start", maxWidth: "1200px" }}
      >
        {otherColumns.map((col, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              width: "auto",
            }}
          >
            {col.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} style={{ textDecoration: "none" }}>
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "13px",
                    color: "rgb(30,30,30)",
                    textAlign: "left",
                  }}
                >
                  {p.city && p.city !== p.name ? `${p.city} - ${p.name}` : p.name}
                </p>
              </Link>
            ))}
          </div>
        ))}
      </section>

      <Footer />
    </div>
  );
}

// ── SZKIELET NA CZAS ŁADOWANIA ───────────────────────────────────────────────

function LocationSkeleton() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        gap: "30px",
      }}
      aria-busy="true"
      aria-label="Wczytywanie lokalizacji"
    >
      <div
        style={{
          width: "100%",
          height: "380px",
          backgroundColor: "rgba(0,0,0,0.06)",
        }}
      />
      {[60, 24, 24].map((h, i) => (
        <span
          key={i}
          style={{
            display: "block",
            height: `${h}px`,
            width: i === 0 ? "40%" : "70%",
            maxWidth: "700px",
            borderRadius: "10px",
            backgroundColor: "rgba(0,0,0,0.05)",
          }}
        />
      ))}
    </div>
  );
}